import { useMutation, useQuery } from '@tanstack/react-query'
import {
  createProductList,
  getProductListItemsAsPlanItems,
  getProductListItems,
  getProductList,
  isMismatchError,
  isAutoCorrection,
  type ProcessingResponse,
  type ProductListRead,
  type ProductListItemRead,
  type ValidationError,
  type PaginatedProductListItems,
} from '@/api/productLists'
import type { PlanItem } from '@/api/planItems'

export interface CreateProductListParams {
  fileId: string
  name: string
  description?: string
}

/**
 * Mutation hook for creating a product list from an uploaded file
 * Returns processing info with SSE URL for status updates
 */
export function useCreateProductList() {
  return useMutation<ProcessingResponse, Error, CreateProductListParams>({
    mutationFn: createProductList,
  })
}

/**
 * Query hook for fetching a product list by ID
 */
export function useProductList(productListId: string | null) {
  return useQuery<ProductListRead, Error>({
    queryKey: ['productList', productListId],
    queryFn: () => getProductList(productListId!),
    enabled: !!productListId,
  })
}

/**
 * Query hook for fetching product list items with pagination
 * Maps items to PlanItem format for display in the wizard
 */
export function useProductListItems(
  productListId: string | null,
  page: number = 1,
  pageSize: number = 5
) {
  return useQuery<
    {
      items: PlanItem[]
      totalCount: number
      page: number
      pageSize: number
      totalPages: number
    },
    Error
  >({
    queryKey: ['productListItems', productListId, page, pageSize],
    queryFn: () =>
      getProductListItemsAsPlanItems(productListId!, page, pageSize),
    enabled: !!productListId,
    staleTime: 0, // Always fetch fresh data
  })
}

/**
 * Query hook for fetching rejected items (is_valid=false)
 */
export function useProductListRejectedItems(
  productListId: string | null,
  page: number = 1,
  pageSize: number = 5,
  enabled: boolean = true
) {
  return useQuery<PaginatedProductListItems, Error>({
    queryKey: ['productListRejectedItems', productListId, page, pageSize],
    queryFn: () => getProductListItems(productListId!, page, pageSize, false),
    enabled: !!productListId && enabled,
    staleTime: 0,
  })
}

/** Max valid rows to scan for issues before giving up and flagging `truncated`. */
const ISSUES_SCAN_ROW_CAP = 5000
const ISSUES_SCAN_PAGE_SIZE = 200

export interface MismatchColumnSummary {
  column: string
  count: number
  values: Set<string>
}

export interface HierarchyCorrectionMapping {
  id: string
  correction: ValidationError
  rowNumbers: number[]
}

export interface ProductListIssueBreakdown {
  /** Every scanned valid row, with or without issues, in file order. */
  rows: ProductListItemRead[]
  /** All valid rows that carry any error/warning. */
  items: ProductListItemRead[]
  /** Subset: contains a DROPDOWN_VALUE_NOT_FOUND / HIERARCHY_VALUE_NOT_FOUND error. */
  mismatchItems: ProductListItemRead[]
  /** Rows with automatic hierarchy / vendor name corrections, containing only those warnings. */
  correctionItems: ProductListItemRead[]
  /** Unique field + original value + corrected value mappings across all scanned rows. */
  correctionMappings: HierarchyCorrectionMapping[]
  /** Subset: has errors, but none are mismatch or hierarchy-correction codes. */
  otherWarningItems: ProductListItemRead[]
  /** Per-column breakdown of mismatch errors, in first-seen order. */
  mismatchByColumn: MismatchColumnSummary[]
  /** True if the row cap was hit before scanning every valid row. */
  truncated: boolean
}

/**
 * Pages through every valid row of a product list (not just a sample) and
 * groups issues into unresolved mismatches, automatic hierarchy / vendor name corrections
 * (deduped by mapping) and other (non-blocking) warnings. A full scan —
 * rather than a capped sample — is required because mismatches must block
 * the entire import, so missing one on a later page would let a bad file
 * through.
 */
export function useProductListIssues(
  productListId: string | null,
  validRowsCount: number,
  enabled: boolean = true
) {
  return useQuery<ProductListIssueBreakdown, Error>({
    queryKey: ['productListIssues', productListId, validRowsCount],
    queryFn: async () => {
      const items: ProductListItemRead[] = []
      let truncated = false
      const totalPages = Math.ceil(validRowsCount / ISSUES_SCAN_PAGE_SIZE)

      for (let page = 1; page <= totalPages; page++) {
        if (items.length >= ISSUES_SCAN_ROW_CAP) {
          truncated = true
          break
        }
        const response = await getProductListItems(
          productListId!,
          page,
          ISSUES_SCAN_PAGE_SIZE,
          true
        )
        items.push(...response.data)
        if (response.data.length === 0) break
      }

      const withIssues = items.filter(
        item => item.errors && item.errors.length > 0
      )
      const mismatchItems: ProductListItemRead[] = []
      const correctionItems: ProductListItemRead[] = []
      const correctionMappings = new Map<
        string,
        { correction: ValidationError; rowNumbers: Set<number> }
      >()
      const otherWarningItems: ProductListItemRead[] = []
      const byColumn = new Map<string, MismatchColumnSummary>()

      for (const item of withIssues) {
        const mismatchErrors = item.errors!.filter(isMismatchError)
        if (mismatchErrors.length > 0) {
          mismatchItems.push({ ...item, errors: mismatchErrors })
          for (const err of mismatchErrors) {
            const existing = byColumn.get(err.column)
            if (existing) {
              existing.count += 1
              if (err.value) existing.values.add(err.value)
            } else {
              byColumn.set(err.column, {
                column: err.column,
                count: 1,
                values: new Set(err.value ? [err.value] : []),
              })
            }
          }
        }

        const corrections = item.errors!.filter(isAutoCorrection)
        if (corrections.length > 0) {
          correctionItems.push({ ...item, errors: corrections })
          for (const correction of corrections) {
            const key = JSON.stringify([
              correction.column,
              correction.value,
              correction.corrected_value ?? null,
            ])
            const existing = correctionMappings.get(key)
            if (existing) {
              existing.rowNumbers.add(item.row_number)
            } else {
              correctionMappings.set(key, {
                correction,
                rowNumbers: new Set([item.row_number]),
              })
            }
          }
        }

        const otherWarnings = item.errors!.filter(
          error => !isMismatchError(error) && !isAutoCorrection(error)
        )
        if (otherWarnings.length > 0) {
          otherWarningItems.push({ ...item, errors: otherWarnings })
        }
      }

      return {
        rows: items,
        items: withIssues,
        mismatchItems,
        correctionItems,
        correctionMappings: Array.from(correctionMappings, ([id, mapping]) => ({
          id,
          correction: mapping.correction,
          rowNumbers: [...mapping.rowNumbers].sort((a, b) => a - b),
        })),
        otherWarningItems,
        mismatchByColumn: Array.from(byColumn.values()),
        truncated,
      }
    },
    enabled: !!productListId && enabled && validRowsCount > 0,
    staleTime: 0,
  })
}
